import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { keepIndex } from './helper';

function preloadImage(src, callback) {
  const img = new Image();
  img.onload = () => callback(img);
  img.src = src;
}

function drawImageScaled(img, ctx) {
  const canvas = ctx.canvas;
  const w = canvas.width;
  const h = canvas.height;
  const iw = img.width;
  const ih = img.height;
  const hRatio = w / iw;
  const vRatio = h / ih;
  const ratio = Math.max(hRatio, vRatio);
  const centerShift_x = (w - iw * ratio) / 2;
  const centerShift_y = (h - ih * ratio) / 2;
  // ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0, iw, ih, centerShift_x, centerShift_y, iw * ratio, ih * ratio);
}

function Player({ width, height, currentFrame, frames = [], onload, active, name = '' }) {
  // const fps = 24;
  const lastFrame = useRef(currentFrame);
  const canvas = useRef(null);
  const [ctx, setContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState({
    loadingComplete: false,
    totalLoaded: 0,
    totalFramesToLoad: frames.length
  });
  const [htmlImageElements, setHtmlImageElements] = useState(null);

  const canDraw = useMemo(() => {
    if (!active) {
      return false;
    }
    if (loading || !loadingProgress.loadingComplete) {
      return false;
    }
    if (!canvas || !canvas.current || !ctx) {
      return false;
    }
    if (!htmlImageElements || !htmlImageElements.length) {
      return false;
    }
    return true;
  }, [loading, loadingProgress.loadingComplete, canvas, htmlImageElements, ctx, active]);

  useEffect(() => {
    if (!canDraw) {
      return;
    }
    const img = htmlImageElements[currentFrame];
    if (!img) {
      return;
    }
    drawImageScaled(img, ctx);
    lastFrame.current = currentFrame;
  }, [canDraw, width, height, currentFrame, ctx, htmlImageElements, lastFrame, frames.length]);

  useEffect(() => {
    if (!canvas.current) {
      setContext(null);
      return;
    }
    setContext(canvas.current.getContext('2d'));
  }, [canvas, frames]);

  useEffect(() => {
    if (!active) {
      return;
    }
    if (loading.loadingComplete) {
      return;
    }
    const htmlImageElements = [];
    frames.forEach((frame, index) => {
      preloadImage(frame, img => {
        // ensure order is correct
        htmlImageElements.splice(index, 0, img);

        setLoadingProgress(draft => ({
          ...draft,
          totalLoaded: htmlImageElements.length
        }));

        if (htmlImageElements.length === frames.length) {
          setLoading(false);
          htmlImageElements.sort((a, b) => keepIndex(a.src) - keepIndex(b.src));
          setHtmlImageElements(htmlImageElements);
          setLoadingProgress(draft => ({
            ...draft,
            loadingComplete: true
          }));
        }
      });
    });
  }, [frames, active, loading.loadingComplete]);

  useEffect(() => {
    if (!loading && loadingProgress.loadingComplete) {
      // console.log('loading: ', loading, 'loadingProgress.loadingComplete: ', loadingProgress.loadingComplete);
      onload();
    }
  }, [loadingProgress.loadingComplete, loading, onload]);

  return <canvas ref={canvas} width={width} height={height} />;
}

Player.propTypes = {
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  currentFrame: PropTypes.number.isRequired,
  frames: PropTypes.arrayOf(PropTypes.string).isRequired,
  onload: PropTypes.func.isRequired,
  active: PropTypes.bool,
  name: PropTypes.string
};

export default Player;
